import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Filler,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import React from 'react';

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Filler, Tooltip, Legend);

interface BusynessPredictionChartProps {
  labels: string[];
  dataPoints: number[];
  currentTimeIndex?: string; // Format: "14:00" or "14h"
  currentCapacity?: number; // Current capacity percentage for the vertical line
}

const BusynessPredictionChart: React.FC<BusynessPredictionChartProps> = ({ 
  labels, 
  dataPoints, 
  currentTimeIndex,
  currentCapacity
}) => {
  // Format labels to 24-hour format (e.g., "14:00:00" -> "14h")
  const formatLabel = (label: string): string => {
    if (!label) return '';
    // Extract hour from time string (e.g., "14:00:00" -> "14")
    const hour = label.split(':')[0];
    return `${hour}h`;
  };

  // Convert current time index to match label format
  const getCurrentTimeLabel = (): string | undefined => {
    if (!currentTimeIndex) return undefined;
    // Handle both "14:00" and "14h" formats
    const hour = currentTimeIndex.replace('h', '').split(':')[0];
    return `${hour}h`;
  };

  const formattedLabels = labels.map(formatLabel);
  const currentTimeLabel = getCurrentTimeLabel();

  // Helper function to get busyness label - matches LiveStatusCard definitions
  const getBusynessLabel = (capacity: number): string => {
    if (capacity === undefined || capacity === null || capacity === 0) return 'Closed';
    if (capacity < 30) return 'Very Quiet';
    if (capacity < 60) return 'Moderate';
    if (capacity < 80) return 'Busy';
    return 'Very Busy';
  };

  // Helper function to get busyness color
  const getBusynessColor = (capacity: number): string => {
    if (capacity === undefined || capacity === null || capacity === 0) return '#64748b';
    if (capacity < 30) return '#10b981'; // Green
    if (capacity < 60) return '#f59e0b'; // Orange
    if (capacity < 80) return '#fb923c'; // Orange-red
    return '#ef4444'; // Red
  };

  const data: ChartData<'line'> = {
    labels: formattedLabels,
    datasets: [
      {
        label: 'Busyness',
        data: dataPoints,
        fill: true,
        backgroundColor: 'rgba(124, 58, 237, 0.1)', // VibeCheck purple with transparency
        borderColor: '#7c3aed', // VibeCheck purple
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: '#7c3aed',
        pointHoverBorderColor: '#ffffff',
        pointHoverBorderWidth: 2,
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          size: 13,
        },
        callbacks: {
          title: (context) => {
            return context[0].label; // Show time label (e.g., "19h")
          },
          label: (context) => {
            const capacity = context.parsed.y;
            const busynessLabel = getBusynessLabel(capacity);
            return `${capacity}% ${busynessLabel}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.08)',
        },
        ticks: {
          font: {
            size: 11,
          },
          color: '#64748b',
        },
      },
      y: {
        display: false,
        beginAtZero: true,
        max: 105,
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
  };

  // Find index of current time for vertical line positioning
  const currentTimeIndexPos = currentTimeLabel ? formattedLabels.indexOf(currentTimeLabel) : -1;
  
  // Get current capacity value at the vertical line position
  const currentCapacityValue = currentCapacity !== undefined && currentCapacity !== null 
    ? currentCapacity 
    : (currentTimeIndexPos >= 0 && currentTimeIndexPos < dataPoints.length 
        ? dataPoints[currentTimeIndexPos] 
        : undefined);
  
  const currentBusynessLabel = currentCapacityValue !== undefined 
    ? getBusynessLabel(currentCapacityValue) 
    : null;

  return (
    <div className="busyness-prediction-chart-container">
      <Line data={data} options={options} />
      {/* Vertical line at current time */}
      {currentTimeIndexPos >= 0 && formattedLabels.length > 0 && currentCapacityValue !== undefined && (
        <div 
          className="busyness-prediction-current-time-line"
          style={{
            left: `${((currentTimeIndexPos + 0.5) / formattedLabels.length) * 100}%`,
          }}
        />
      )}
    </div>
  );
};

export default BusynessPredictionChart;
